import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  OPEN_SETTINGS_EVENT,
  currentConsent,
  getStoredConsent,
  requiresOptIn,
  saveConsent,
} from "../../utils/consent";
import styles from "./CookieConsent.module.css";

// Shown once to every visitor until they choose; reopened from the footer's
// "Cookie settings" link or the privacy policy. Starting state (before any
// choice) is set in public/index.html: off for EEA/UK/CH, on elsewhere.
// Partner-branded candidate pages are white-label: don't name Awasource there.
const WHITE_LABEL = /^\/(assessment|enroll)(\/|$)/;

export default function CookieConsent() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => !getStoredConsent());
  const [details, setDetails] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    const c = currentConsent();
    // In opt-in regions the toggles start off; elsewhere they show what's running.
    return { analytics: c.analytics, ads: c.ads };
  });
  const firstButton = useRef(null);

  useEffect(() => {
    const onOpen = () => {
      const c = currentConsent();
      setPrefs({ analytics: c.analytics, ads: c.ads });
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (open && details && firstButton.current) firstButton.current.focus();
  }, [open, details]);

  if (!open) return null;

  const choose = (choice) => {
    saveConsent(choice);
    setOpen(false);
    setDetails(false);
  };

  const optIn = requiresOptIn();

  return (
    <div
      className={styles.banner}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-text"
    >
      <p id="cookie-consent-title" className={styles.title}>{WHITE_LABEL.test(pathname) ? "Cookies on this site" : "Cookies on Awasource"}</p>
      <p id="cookie-consent-text" className={styles.text}>
        We use cookies to make our site work, understand how it's used and improve your
        experience.
        {optIn
          ? " Choose \"Accept all\" to allow all cookies, or pick what you're happy with in Settings."
          : " You can change your choice at any time in Settings."}{" "}
        <Link to="/privacy-policy#cookies" className={styles.link}>Privacy policy</Link>
      </p>

      {details && (
        <fieldset className={styles.prefs}>
          <legend className={styles.srOnly}>Cookie preferences</legend>
          <label className={styles.row}>
            <input type="checkbox" checked disabled />
            <span>
              <strong>Essential</strong>
              <small>Needed for the site to work. Always on.</small>
            </span>
          </label>
          <label className={styles.row}>
            <input
              type="checkbox"
              checked={prefs.analytics}
              onChange={(e) => {
                const on = e.target.checked;
                setPrefs((p) => ({ ...p, analytics: on }));
              }}
            />
            <span>
              <strong>Analytics</strong>
              <small>Help us understand how the site is used so we can improve it.</small>
            </span>
          </label>
          <label className={styles.row}>
            <input
              type="checkbox"
              checked={prefs.ads}
              onChange={(e) => {
                const on = e.target.checked;
                setPrefs((p) => ({ ...p, ads: on }));
              }}
            />
            <span>
              <strong>Advertising</strong>
              <small>Help us show relevant ads and measure how they perform.</small>
            </span>
          </label>
        </fieldset>
      )}

      <div className={styles.actions}>
        {details ? (
          <button
            type="button"
            ref={firstButton}
            className={styles.secondary}
            onClick={() => choose(prefs)}
          >
            Save choices
          </button>
        ) : (
          <button type="button" className={styles.ghost} onClick={() => setDetails(true)}>
            Settings
          </button>
        )}
        <button
          type="button"
          className={styles.secondary}
          onClick={() => choose({ analytics: false, ads: false })}
        >
          Reject non-essential
        </button>
        <button
          type="button"
          className={styles.primary}
          onClick={() => choose({ analytics: true, ads: true })}
        >
          Accept all
        </button>
      </div>
    </div>
  );
}
